import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AuthUser } from '../types/UserRole';

// Lazy import to avoid circular dependency: store → authSlice → apiClient → store
// eslint-disable-next-line @typescript-eslint/no-require-imports
const getApiClient = () => require('../services/apiClient').apiClient;

interface AuthState {
    user: AuthUser | null;
    accessToken: string | null;
    refreshToken: string | null;
    isAuthenticated: boolean;
    loading: boolean;
    error: string | null;
}

const initialState: AuthState = {
    user: null,
    accessToken: localStorage.getItem('accessToken'),
    refreshToken: localStorage.getItem('refreshToken'),
    isAuthenticated: !!localStorage.getItem('accessToken'),
    loading: false,
    error: null,
};

export const loginThunk = createAsyncThunk(
    'auth/login',
    async (credentials: { email: string; password: string }, { rejectWithValue }) => {
        try {
            const { data } = await getApiClient().post('/auth/login', credentials);
            return data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.message ?? 'Login failed');
        }
    }
);

export const getMeThunk = createAsyncThunk(
    'auth/getMe',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await getApiClient().get('/auth/me');
            return data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.message ?? 'Failed to fetch user');
        }
    }
);

export const logoutThunk = createAsyncThunk(
    'auth/logout',
    async (_, { getState }) => {
        try {
            const state = getState() as { auth: AuthState };
            await getApiClient().post('/auth/logout', { refreshToken: state.auth.refreshToken });
        } catch {
            // Always clear local state even if server call fails
        }
    }
);

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setTokens(state, action: PayloadAction<{ accessToken: string; refreshToken: string }>) {
            state.accessToken = action.payload.accessToken;
            state.refreshToken = action.payload.refreshToken;
            state.isAuthenticated = true;
            localStorage.setItem('accessToken', action.payload.accessToken);
            localStorage.setItem('refreshToken', action.payload.refreshToken);
        },
        clearAuth(state) {
            state.user = null;
            state.accessToken = null;
            state.refreshToken = null;
            state.isAuthenticated = false;
            state.error = null;
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('adminLoginAt');
        },
        clearError(state) {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loginThunk.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(loginThunk.fulfilled, (state, action) => {
                const payload = action.payload as { user: AuthUser; accessToken: string; refreshToken: string };
                state.loading = false;
                state.user = payload.user;
                state.accessToken = payload.accessToken;
                state.refreshToken = payload.refreshToken;
                state.isAuthenticated = true;
                localStorage.setItem('accessToken', payload.accessToken);
                localStorage.setItem('refreshToken', payload.refreshToken);
                localStorage.setItem('adminLoginAt', new Date().toISOString());
            })
            .addCase(loginThunk.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(getMeThunk.fulfilled, (state, action) => {
                state.user = action.payload as AuthUser;
                state.isAuthenticated = true;
            })
            .addCase(getMeThunk.rejected, (state) => {
                state.user = null;
                state.isAuthenticated = false;
                state.accessToken = null;
                state.refreshToken = null;
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('adminLoginAt');
            })
            .addCase(logoutThunk.fulfilled, (state) => {
                state.user = null;
                state.accessToken = null;
                state.refreshToken = null;
                state.isAuthenticated = false;
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('adminLoginAt');
            });
    },
});

export const { setTokens, clearAuth, clearError } = authSlice.actions;
export default authSlice.reducer;
